/**
 * Banner — the single section/page-level notice strip (see DESIGN.md "Banners").
 *
 * Kept in lockstep with admin/src/components/ui/Banner.tsx — same API and
 * variant tokens; only the icon source differs (web uses lucide-react, admin
 * uses its inline-SVG shim).
 */
import type { ReactNode } from "react";
import { AlertTriangle, Info, X } from "lucide-react";
import styles from "./Banner.module.css";

export type BannerVariant = "info" | "warning" | "error";

interface BannerProps {
  variant: BannerVariant;
  /** Bold first line. Optional — short banners can be body-only children. */
  title?: string;
  children?: ReactNode;
  /**
   * Custom leading icon node. Defaults to the variant icon; pass false to
   * render no icon.
   */
  icon?: ReactNode | false;
  /** Renders the X button. The caller owns what dismissal means/persists. */
  onDismiss?: () => void;
  /** Accessible label for the X button. */
  dismissLabel?: string;
  /** Disables the X while a persisted dismiss is in flight. */
  dismissing?: boolean;
  className?: string;
  "data-testid"?: string;
}

function defaultIcon(variant: BannerVariant, className: string): ReactNode {
  switch (variant) {
    case "info":
      return <Info className={className} />;
    case "warning":
    case "error":
      return <AlertTriangle className={className} />;
  }
}

export function Banner({
  variant,
  title,
  children,
  icon,
  onDismiss,
  dismissLabel = "Dismiss",
  dismissing = false,
  className = "",
  "data-testid": testId,
}: BannerProps) {
  const iconNode =
    icon === false ? null : (icon ?? defaultIcon(variant, styles.icon));

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={`${styles.banner} ${styles[variant]} ${className}`}
      data-testid={testId}
    >
      <div className={styles.row}>
        {iconNode}
        <div className={styles.body}>
          {title && <p className={styles.title}>{title}</p>}
          {children}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            disabled={dismissing}
            aria-label={dismissLabel}
            className={styles.dismiss}
          >
            <X className={styles.dismissIcon} />
          </button>
        )}
      </div>
    </div>
  );
}
